import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
  Upload,
  X,
  Image as ImageIcon,
  Video,
  Play,
  Plus,
  Trash2,
  AlertTriangle,
  Link,
} from "lucide-react";
import { uploadApi } from "../../services/api";
import toast from "react-hot-toast";
import { useAuthStore } from "../../stores/authStore";

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
// O backend recomprime o vídeo para no máximo 5MB (limite do bucket), então o
// teto aqui é só para não subir um master gigante à toa. O que realmente
// determina se cabe nos 5MB é a duração.
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
const MAX_VIDEO_DURATION_SECONDS = 120;

const formatMb = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)}MB`;

/**
 * Lê a duração do vídeo no próprio browser para barrar arquivos longos antes de
 * gastar o upload. Devolve NaN quando o browser não consegue ler os metadados —
 * nesse caso deixamos o backend decidir.
 */
const readVideoDuration = (file: File): Promise<number> =>
  new Promise((resolve) => {
    const video = document.createElement("video");
    const objectUrl = URL.createObjectURL(file);
    const finish = (duration: number) => {
      URL.revokeObjectURL(objectUrl);
      resolve(duration);
    };
    video.preload = "metadata";
    video.onloadedmetadata = () => finish(video.duration);
    video.onerror = () => finish(NaN);
    video.src = objectUrl;
  });

/** Limite por tipo: o dropzone só aceita um `maxSize` global. */
const validateFileSize = (file: File) => {
  const isVideo = file.type.startsWith("video/");
  const max = isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
  if (file.size <= max) return null;
  return {
    code: "file-too-large",
    message: `${isVideo ? "Vídeo" : "Imagem"} deve ter no máximo ${max / (1024 * 1024)}MB (o seu tem ${formatMb(file.size)})`,
  };
};

interface ImageUploaderProps {
  currentImage?: string;
  currentThumbnailUrl?: string;
  onImageSelect: (
    url: string,
    type?: "image" | "video",
    thumbnailUrl?: string,
  ) => void;
  type: "desktop" | "mobile";
  pairedType?: "image" | "video"; // Tipo já selecionado no desktop/mobile pareado
  pairedHasValue?: boolean; // Se o desktop/mobile pareado já tem um valor
}

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (
    url: string,
    type?: "image" | "video",
    thumbnailUrl?: string,
  ) => void;
  type: "desktop" | "mobile";
  currentImage?: string;
}

interface MediaItem {
  filename: string;
  url: string;
  uploadedAt: Date;
  type: "image" | "video";
  thumbnailUrl?: string;
}

// Modal para seleção de imagens e vídeos
function ImageModal({ isOpen, onClose, onSelect, type, currentImage }: ImageModalProps) {
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>("");
  const [deletingMedia, setDeletingMedia] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState<string>("");
  const storeName = useAuthStore((state) => state.user?.storeName);

  // Novo estado para controlar a aba ativa (Imagens ou Vídeos)
  const [activeTab, setActiveTab] = useState<"image" | "video">("image");

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Carregar mídia existente
  const loadMedia = useCallback(
    async (pageNum: number = 1) => {
      // Se não for a primeira página, verifica se há mais para carregar
      if (pageNum > 1 && !hasMore) return;

      if (pageNum === 1) setIsLoading(true);
      else setLoadingMore(true);

      if (!storeName) {
        toast.error(
          "Store não identificada. Não foi possível carregar a mídia.",
        );
        setIsLoading(false);
        setLoadingMore(false);
        return;
      }
      try {
        // Passar 'type' (desktop/mobile) e 'activeTab' (image/video) para filtrar no backend
        const response = await uploadApi.listMedia(
          storeName,
          type,
          pageNum,
          32,
          activeTab,
        );

        if (pageNum === 1) {
          setMedia(response.media || []);
        } else {
          setMedia((prev) => [...prev, ...(response.media || [])]);
        }

        setHasMore(response.pagination ? response.pagination.hasMore : false);
        setPage(pageNum);
      } catch (error) {
        console.error("Erro ao carregar mídia:", error);
      } finally {
        setIsLoading(false);
        setLoadingMore(false);
      }
    },
    [storeName, type, activeTab], // Adicionado activeTab como dependência
  );

  // Resetar e carregar quando mudar a aba
  React.useEffect(() => {
    if (isOpen) {
      setPage(1);
      setHasMore(true);
      setMedia([]); // Limpar mídia anterior ao trocar de aba
      loadMedia(1);
    }
  }, [activeTab, isOpen, loadMedia]);

  // Abrir modal (limpar input de URL)
  React.useEffect(() => {
    if (isOpen) {
      setUrlInput(""); // Limpar URL quando abrir modal
    }
  }, [isOpen]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      hasMore &&
      !loadingMore &&
      !isLoading
    ) {
      loadMedia(page + 1);
    }
  };

  // Upload de nova mídia
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      setUploading(true);
      setUploadProgress("Processando arquivo...");

      if (!storeName) {
        toast.error("Store não identificada. Upload cancelado.");
        setUploading(false);
        return;
      }

      try {
        const file = acceptedFiles[0];
        const isVideo = file.type.startsWith("video/");

        if (isVideo) {
          setUploadProgress("Lendo o vídeo...");
          const duration = await readVideoDuration(file);
          if (
            Number.isFinite(duration) &&
            duration > MAX_VIDEO_DURATION_SECONDS
          ) {
            toast.error(
              `O vídeo tem ${Math.round(duration)}s e o máximo é ${MAX_VIDEO_DURATION_SECONDS}s — acima disso não cabe no limite de 5MB depois da compressão.`,
            );
            setUploading(false);
            setUploadProgress("");
            return;
          }

          setUploadProgress("Enviando vídeo... 0%");
          const response = await uploadApi.uploadVideo(file, type, storeName, {
            onProgress: (percent) =>
              setUploadProgress(
                percent < 100
                  ? `Enviando vídeo... ${percent}%`
                  : "Otimizando vídeo, isso pode levar até 1 minuto...",
              ),
          });

          if (response.success) {
            setUploadProgress(
              response.originalSize
                ? `Vídeo enviado! ${formatMb(response.originalSize)} → ${formatMb(response.size)}`
                : "Vídeo enviado com sucesso!",
            );
            setTimeout(() => {
              loadMedia();
              // Não precisa mais selecionar automaticamente aqui
              setUploading(false);
              setUploadProgress("");
            }, 2000);
          } else {
            setUploadProgress("Erro ao enviar vídeo");
            setTimeout(() => {
              setUploading(false);
              setUploadProgress("");
            }, 2000);
          }
        } else {
          setUploadProgress("Enviando imagem...");
          const response = await uploadApi.uploadImage(file, type, storeName);

          if (response.success) {
            setUploadProgress("Imagem enviada com sucesso!");
            setTimeout(() => {
              loadMedia();
              setUploading(false);
              setUploadProgress("");
            }, 1000);
          } else {
            setUploadProgress("Erro ao enviar imagem");
            setTimeout(() => {
              setUploading(false);
              setUploadProgress("");
            }, 2000);
          }
        }
      } catch (error: any) {
        console.error("Erro no upload:", error);

        // O backend responde 4xx com { error, details } para tudo que o usuário
        // consegue corrigir (arquivo grande demais, vídeo longo, formato ilegível)
        const details = error.response?.data?.details;
        if (details) {
          toast.error(details);
          setUploadProgress(error.response?.data?.error || "Erro no upload");
        } else if (error.response?.status === 413) {
          // 413 sem corpo JSON = veio do proxy (client_max_body_size), não do backend
          toast.error(
            "O servidor recusou o arquivo por ser grande demais. Envie um vídeo menor.",
          );
          setUploadProgress("Arquivo muito grande");
        } else if (error.code === "ECONNABORTED") {
          toast.error(
            "O processamento demorou demais e foi cancelado. Tente um vídeo mais curto.",
          );
          setUploadProgress("Tempo esgotado");
        } else {
          toast.error("Erro no upload. Tente novamente.");
          setUploadProgress("Erro no upload");
        }

        setTimeout(() => {
          setUploading(false);
          setUploadProgress("");
        }, 2000);
      }
    },
    [type, loadMedia, storeName],
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      onDrop,
      accept: {
        "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp"],
        "video/*": [".mp4", ".webm", ".ogg", ".mov", ".avi", ".mkv"],
      },
      validator: validateFileSize,
      multiple: false,
      onDropRejected: (rejectedFiles) => {
        rejectedFiles.forEach(({ errors }) => {
          errors.forEach((error) => {
            if (error.code === "file-too-large") {
              toast.error(error.message);
            } else if (error.code === "file-invalid-type") {
              toast.error("Tipo de arquivo não suportado");
            } else {
              toast.error("Erro ao processar arquivo");
            }
          });
        });
      },
    });

  // Função para detectar tipo de mídia pela extensão da URL
  const detectMediaType = (url: string): "image" | "video" => {
    const extension = url.split(".").pop()?.toLowerCase();
    const videoExtensions = ["mp4", "webm", "ogg", "mov", "avi", "mkv"];
    return videoExtensions.includes(extension || "") ? "video" : "image";
  };

  // Adicionar URL
  const handleAddUrl = async () => {
    if (!urlInput.trim()) {
      toast.error("Por favor, insira uma URL válida");
      return;
    }

    try {
      const mediaType = detectMediaType(urlInput);
      // Ao adicionar via URL, não temos uma thumbnail explícita
      onSelect(urlInput, mediaType, undefined);
      onClose();
      toast.success(
        `${mediaType === "video" ? "Vídeo" : "Imagem"} adicionada com sucesso!`,
      );
    } catch (error) {
      console.error("Erro ao adicionar URL:", error);
      toast.error("Erro ao adicionar URL");
    }
  };

  // Modal de confirmação de exclusão
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    media: MediaItem | null;
    components: string[];
  }>({
    isOpen: false,
    media: null,
    components: [],
  });

  // Função para excluir mídia
  const handleDeleteMedia = async (media: MediaItem, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!storeName) {
      toast.error("Store não identificada. Não foi possível excluir a mídia.");
      return;
    }

    try {
      setDeletingMedia(media.filename);

      // Verificar se a mídia está sendo usada
      const usage = await uploadApi.checkMediaUsage(media.filename, storeName);

      if (usage.isUsed) {
        setDeleteModal({
          isOpen: true,
          media,
          components: usage.components,
        });
        setDeletingMedia(null);
        return;
      }

      await performDelete(media);
    } catch (error) {
      console.error("Erro ao verificar uso da mídia:", error);
      toast.error("Erro ao verificar uso da mídia");
      setDeletingMedia(null);
    }
  };

  // Função para executar a exclusão
  const performDelete = async (media: MediaItem) => {
    try {
      setDeletingMedia(media.filename);

      if (!storeName) {
        toast.error(
          "Store não identificada. Não foi possível excluir a mídia.",
        );
        setDeletingMedia(null);
        return;
      }

      await uploadApi.deleteMedia(media.filename, storeName);
      await loadMedia();

      toast.success("Mídia excluída com sucesso!");
    } catch (error) {
      console.error("Erro ao excluir mídia:", error);
      toast.error("Erro ao excluir mídia");
    } finally {
      setDeletingMedia(null);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-md max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">
              Selecionar {type === "desktop" ? "Desktop" : "Mobile"}
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="flex h-[calc(90vh-120px)]">
            {/* Lista de mídia existente */}
            <div
              className="w-2/3 p-4 border-r overflow-y-auto"
              onScroll={handleScroll}
            >
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Mídia Disponível
              </h4>

              {/* Tabs de Seleção de Tipo de Mídia */}
              <div className="flex border-b border-gray-200 mb-4">
                <button
                  className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === "image"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                  onClick={() => setActiveTab("image")}
                >
                  <div className="flex items-center space-x-2">
                    <ImageIcon className="w-4 h-4" />
                    <span>Imagens</span>
                  </div>
                </button>
                <button
                  className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === "video"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                  onClick={() => setActiveTab("video")}
                >
                  <div className="flex items-center space-x-2">
                    <Video className="w-4 h-4" />
                    <span>Vídeos</span>
                  </div>
                </button>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                </div>
              ) : media.length === 0 ? (
                <div className="text-center py-8">
                  <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    Nenhuma mídia encontrada
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-3">
                  {media.map((item, index) => {
                    const mediaUrl = item.url;
                    const thumbnailUrl = item.thumbnailUrl;

                    return (
                      <div
                        key={`${item.filename}-${index}`}
                        className={`relative group cursor-pointer rounded-md overflow-hidden transition-colors ${
                          mediaUrl === currentImage
                            ? "border-2 border-primary-500"
                            : "border hover:border-primary-500"
                        }`}
                        onClick={() =>
                          onSelect(mediaUrl, item.type, thumbnailUrl)
                        }
                      >
                        {item.type === "video" ? (
                          <div className="relative w-full h-24 bg-gray-100 flex items-center justify-center">
                            {/* Thumbnail do vídeo baseada na URL fornecida pela API */}
                            {thumbnailUrl ? (
                              <img
                                src={thumbnailUrl}
                                alt={`Thumbnail para ${item.filename}`}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="text-gray-400 p-4">
                                <Video className="w-8 h-8 mx-auto" />
                                <span className="text-xs mt-1">
                                  Sem preview
                                </span>
                              </div>
                            )}
                            {/* Overlay com ícone de play */}
                            <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                              <div className="w-8 h-8 bg-white bg-opacity-80 rounded-full flex items-center justify-center">
                                <Play
                                  className="w-4 h-4 text-gray-800 ml-0.5"
                                  fill="currentColor"
                                />
                              </div>
                            </div>
                          </div>
                        ) : (
                          // Miniatura redimensionada pela Supabase; a URL cheia
                          // (mediaUrl) é a que vai pro JSON ao selecionar
                          <img
                            src={thumbnailUrl || mediaUrl}
                            alt={`Mídia ${index + 1}`}
                            className="w-full h-24 object-cover"
                            loading="lazy"
                          />
                        )}

                        {/* Indicador de tipo */}
                        <div className="absolute top-1 left-1 p-1 bg-black bg-opacity-50 text-white rounded text-xs z-10">
                          {item.type === "video" ? (
                            <Video className="w-3 h-3" />
                          ) : (
                            <ImageIcon className="w-3 h-3" />
                          )}
                        </div>

                        {/* Data de upload */}
                        {item.uploadedAt && (
                          <div
                            className="absolute bottom-1 right-1 px-1 py-0.5 bg-black bg-opacity-60 text-white rounded z-10 pointer-events-none"
                            style={{ fontSize: "10px" }}
                          >
                            {(() => {
                              const date = new Date(item.uploadedAt);
                              const day = date
                                .getDate()
                                .toString()
                                .padStart(2, "0");
                              const month = (date.getMonth() + 1)
                                .toString()
                                .padStart(2, "0");
                              const year = date.getFullYear();
                              const hours = date
                                .getHours()
                                .toString()
                                .padStart(2, "0");
                              const minutes = date
                                .getMinutes()
                                .toString()
                                .padStart(2, "0");
                              return `${day}/${month}/${year} ${hours}:${minutes}`;
                            })()}
                          </div>
                        )}

                        {/* Botão de exclusão */}
                        <button
                          onClick={(e) => handleDeleteMedia(item, e)}
                          disabled={deletingMedia === item.filename}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-50 z-20"
                          title="Excluir mídia"
                        >
                          {deletingMedia === item.filename ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          ) : (
                            <Trash2 className="w-3 h-3" />
                          )}
                        </button>

                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center z-0">
                          <span className="text-white opacity-0 group-hover:opacity-100 text-xs font-medium">
                            Selecionar
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  {/* Loader de paginação */}
                  {loadingMore && (
                    <div className="col-span-4 flex justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Painel de upload/URL simplificado */}
            <div className="w-1/3 p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-4">
                Adicionar Nova Mídia
              </h4>

              {/* Upload de arquivo */}
              <div className="mb-6">
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Upload de arquivo
                </label>

                {uploading ? (
                  <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-md">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">{uploadProgress}</p>
                  </div>
                ) : (
                  <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-md p-4 text-center cursor-pointer transition-colors ${
                      isDragActive
                        ? "border-primary-500 bg-primary-50"
                        : "border-gray-300 hover:border-primary-400"
                    }`}
                  >
                    <input {...getInputProps()} />
                    <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 mb-1">
                      Arraste ou clique para selecionar
                    </p>
                    <p className="text-xs text-gray-500">
                      Imagens: PNG, JPG, GIF, WebP até 10MB
                    </p>
                    <p className="text-xs text-gray-500">
                      Vídeos: MP4, WebM, MOV até 100MB e{" "}
                      {MAX_VIDEO_DURATION_SECONDS}s
                    </p>
                  </div>
                )}
              </div>

              {/* Campo de URL */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  URL da mídia
                </label>
                <div className="flex space-x-2">
                  <input
                    type="url"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://exemplo.com/imagem.jpg ou video.mp4"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleAddUrl();
                      }
                    }}
                  />
                  <button
                    onClick={handleAddUrl}
                    className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors text-sm"
                  >
                    <Link className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  O tipo será detectado automaticamente pela extensão
                </p>
              </div>

              {/* Informações de otimização */}
              <div className="mt-6 p-3 bg-gray-50 rounded-md">
                <p className="text-xs font-medium text-gray-700 mb-2">
                  Otimização automática:
                </p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Desktop: máximo 2550px</li>
                  <li>• Mobile: máximo 1024px</li>
                  <li>• Imagens: WebP</li>
                  <li>• GIFs: mantidos como estão, sem perder a animação</li>
                  <li>
                    • Vídeos: convertidos para MP4 (H.264) em até 5MB — 1280px
                    no desktop, 720px no mobile
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de confirmação de exclusão */}
      {deleteModal.isOpen && deleteModal.media && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-md max-w-md w-full mx-4 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Confirmar Exclusão
                </h3>
                <p className="text-sm text-gray-600">
                  Esta mídia está sendo usada
                </p>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-gray-700 mb-2">
                A {deleteModal.media.type === "video" ? "vídeo" : "imagem"}{" "}
                <strong>"{deleteModal.media.filename}"</strong> está sendo usada
                nos seguintes componentes:
              </p>
              <div className="bg-gray-50 rounded-md p-3 max-h-32 overflow-y-auto">
                {deleteModal.components.map((component, index) => (
                  <div key={index} className="text-sm text-gray-600 mb-1">
                    • {component}
                  </div>
                ))}
              </div>
              <p className="text-gray-600 text-sm mt-2">
                Tem certeza que deseja excluí-la mesmo assim?
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() =>
                  setDeleteModal({ isOpen: false, media: null, components: [] })
                }
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  if (deleteModal.media) {
                    await performDelete(deleteModal.media);
                    setDeleteModal({
                      isOpen: false,
                      media: null,
                      components: [],
                    });
                  }
                }}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Excluir Mesmo Assim
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function ImageUploader({
  currentImage,
  currentThumbnailUrl,
  onImageSelect,
  type,
  pairedType,
  pairedHasValue,
}: ImageUploaderProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const storeName = useAuthStore((state) => state.user?.storeName);

  // Função para detectar tipo de mídia pela extensão da URL
  const detectMediaType = (url: string): "image" | "video" => {
    const extension = url.split(".").pop()?.toLowerCase();
    const videoExtensions = ["mp4", "webm", "ogg", "mov", "avi", "mkv"];
    return videoExtensions.includes(extension || "") ? "video" : "image";
  };

  // Função para validar seleção de mídia no modal
  const handleModalSelect = useCallback(
    (url: string, mediaType?: "image" | "video", thumbnailUrl?: string) => {
      console.log("🎯 handleModalSelect chamado:", {
        url,
        mediaType,
        pairedType,
        pairedHasValue,
        thumbnailUrl,
      });

      // Se não há tipo definido, tentar detectar pela URL
      const detectedType = mediaType || detectMediaType(url);

      // Verificar se há conflito de tipos
      if (pairedHasValue && pairedType && pairedType !== detectedType) {
        const pairedTypeName = pairedType === "video" ? "vídeo" : "imagem";
        const currentTypeName = detectedType === "video" ? "vídeo" : "imagem";
        toast.error(
          `Não é possível selecionar ${currentTypeName}. O ${type === "desktop" ? "mobile" : "desktop"} já está configurado como ${pairedTypeName}.`,
        );
        return;
      }

      // Se passou na validação, prosseguir com a seleção
      console.log("✅ Seleção válida, chamando onImageSelect");
      onImageSelect(url, detectedType, thumbnailUrl);
      setIsModalOpen(false);
    },
    [
      pairedHasValue,
      pairedType,
      type,
      onImageSelect,
      setIsModalOpen,
      detectMediaType,
    ],
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      setUploading(true);

      if (!storeName) {
        toast.error("Store não identificada. Upload cancelado.");
        setUploading(false);
        return;
      }

      try {
        const file = acceptedFiles[0];
        const isVideo = file.type.startsWith("video/");

        if (isVideo) {
          const response = await uploadApi.uploadVideo(file, type, storeName);
          if (response.success) {
            console.log("📤 onDrop video success:", {
              url: response.url,
              type: "video",
              thumbnailUrl: response.thumbnailUrl,
            });
            onImageSelect(response.url, "video", response.thumbnailUrl);
          }
        } else {
          const response = await uploadApi.uploadImage(file, type, storeName);
          if (response.success) {
            console.log("📤 onDrop image success:", {
              url: response.url,
              type: "image",
            });
            onImageSelect(response.url, "image");
          }
        }
      } catch (error) {
        console.error("Erro no upload:", error);
      } finally {
        setUploading(false);
      }
    },
    [type, onImageSelect, storeName],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp"],
      "video/*": [".mp4", ".webm", ".ogg", ".mov", ".avi", ".mkv"],
    },
    multiple: false,
    validator: validateFileSize,
  });

  // Detectar tipo da mídia atual
  const currentMediaType = currentImage
    ? detectMediaType(currentImage)
    : "image";

  return (
    <>
      <div className="space-y-3">
        {/* Mídia atual */}
        {currentImage && (
          <div className="relative">
            {currentMediaType === "video" ? (
              <div className="relative w-full h-32 bg-gray-100 rounded-md border overflow-hidden">
                {/* Thumbnail do vídeo se disponível, senão usa a imagem atual */}
                <img
                  src={currentThumbnailUrl || currentImage}
                  alt="Thumbnail do vídeo"
                  className="w-full h-full object-cover"
                />
                {/* Overlay com ícone de play */}
                <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                  <div className="w-8 h-8 bg-white bg-opacity-80 rounded-full flex items-center justify-center">
                    <Play
                      className="w-4 h-4 text-gray-800 ml-0.5"
                      fill="currentColor"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <img
                src={currentImage}
                alt="Mídia atual"
                className="w-full h-32 object-cover rounded-md border"
              />
            )}

            {/* Indicador de tipo */}
            <div className="absolute top-2 left-2 p-1 bg-black bg-opacity-50 text-white rounded text-xs">
              {currentMediaType === "video" ? (
                <Video className="w-3 h-3" />
              ) : (
                <ImageIcon className="w-3 h-3" />
              )}
            </div>

            <button
              onClick={() => onImageSelect("")}
              className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Botões de ação */}
        <div className="flex space-x-2">
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2"
          >
            <ImageIcon className="w-4 h-4" />
            <span className="text-sm">Selecionar</span>
          </button>
        </div>
      </div>

      {/* Modal de seleção */}
      <ImageModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelect={handleModalSelect}
        type={type}
        currentImage={currentImage}
      />
    </>
  );
}
